import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  InputAdornment,
  Alert,
  IconButton,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

const PhoneNumberDialog = ({ open, onSubmit, onClose, loading = false }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");

  const validatePhoneNumber = (phone) => {
    // Basic validation: remove spaces, dashes, parentheses and check for 10-15 digits
    const cleaned = phone.replace(/[\s\-()]/g, "");
    const phoneRegex = /^[+]?[\d]{10,15}$/;
    return phoneRegex.test(cleaned);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow numbers, +, spaces, dashes, and parentheses
    const isValid = /^[0-9+\-\s()]*$/.test(value);
    
    if (isValid) {
      setPhoneNumber(value);
      setError("");
    }
  };

  const handleSubmit = () => {
    setError("");
    
    if (!phoneNumber.trim()) {
      setError("Phone number is required");
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError("Please enter a valid phone number (10-15 digits)");
      return;
    }

    // Send cleaned phone number (remove spaces, dashes, parentheses)
    onSubmit(phoneNumber.replace(/[\s\-()]/g, ""));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Dialog 
      open={open}
      onClose={(event, reason) => {
        // Prevent closing by clicking backdrop or pressing Escape
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        if (!loading) {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 3 },
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          m: { xs: 2, sm: 4 },
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: { xs: 2.5, sm: 3 }, px: { xs: 2.5, sm: 3 }, pr: { xs: 5, sm: 6 }, position: "relative" }}>
        {/* Close icon in top right corner */}
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: { xs: 8, sm: 12 },
            top: { xs: 8, sm: 12 },
            color: "#9ca3af",
            padding: { xs: 0.5, sm: 1 },
            "&:hover": {
              background: "#f3f4f6",
              color: "#6b7280",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.25, sm: 1.5 }, mb: { xs: 0.75, sm: 1 } }}>
          <Box
            sx={{
              width: { xs: 38, sm: 44 },
              height: { xs: 38, sm: 44 },
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <PhoneIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.15rem", sm: "1.3rem" },
                color: "#0f172a",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Welcome! 🎉
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: { xs: "0.82rem", sm: "0.88rem" },
            lineHeight: 1.6,
            mt: { xs: 0.75, sm: 1 },
          }}
        >
          To complete your setup, please provide your phone number.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 }, px: { xs: 2.5, sm: 3 } }}>
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1 },
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              mb: { xs: 2.5, sm: 3 },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#15803d" }} />
            <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, color: "#15803d", fontWeight: 600 }}>
              This is only required once for account verification
            </Typography>
          </Box>

          <TextField
            autoFocus
            fullWidth
            label="Phone Number"
            placeholder="+1 234 567 8900"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onKeyPress={handleKeyPress}
            disabled={loading}
            helperText="Numbers only (can include +, -, spaces, parentheses)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: "#667eea", fontSize: { xs: 18, sm: 20 } }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: { xs: "0.9rem", sm: "1rem" },
                "&.Mui-focused fieldset": {
                  borderColor: "#667eea",
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "0.9rem", sm: "1rem" },
                "&.Mui-focused": {
                  color: "#667eea",
                },
              },
              "& .MuiFormHelperText-root": {
                fontSize: { xs: "0.68rem", sm: "0.7rem" },
                color: "#9ca3af",
                mt: 1,
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: { xs: 1.5, sm: 2 }, borderRadius: 2, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              {error}
            </Alert>
          )}

          <Typography
            sx={{
              mt: { xs: 1.5, sm: 2 },
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              color: "#9ca3af",
              lineHeight: 1.6,
            }}
          >
            We'll use this number to send you important updates and notifications.
            Your privacy is our priority - we never share your information.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 }, pt: { xs: 0.5, sm: 1 }, gap: { xs: 1, sm: 1 } }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: "#6b7280",
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.82rem", sm: "0.88rem" },
            px: { xs: 2, sm: 2.5 },
            py: { xs: 0.75, sm: 1 },
            "&:hover": {
              background: "#f3f4f6",
            },
          }}
        >
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            textTransform: "none",
            fontWeight: 700,
            fontSize: { xs: "0.82rem", sm: "0.88rem" },
            px: { xs: 3, sm: 3.5 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #5568d3 0%, #653a8b 100%)",
              boxShadow: "0 6px 16px rgba(102,126,234,0.4)",
            },
            "&:disabled": {
              background: "#e5e7eb",
              color: "#9ca3af",
            },
          }}
        >
          {loading ? "Completing..." : "Complete Setup"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneNumberDialog;
